/**
 * Shared types for chapter offline drafts, outbox, and 3-way merge conflicts.
 */

import type { ChapterBlockNode, ChapterDocContent } from '../chapterParagraphs';

export type ChapterSyncStatus =
  | 'idle'
  | 'dirty'
  | 'localSaved'
  | 'pushing'
  | 'synced'
  | 'conflict'
  | 'offline'
  | 'error';

export type ChapterDraftRecord = {
  bookId: string;
  chapterId: string;
  userId: string;
  title: string;
  contentJson: string;
  savedAt: number;
  /** Last server-acknowledged revision this draft branched from. */
  baseRev: number;
  baseFingerprint: string;
  /** TipTap JSON snapshot at baseRev (for 3-way merge). */
  baseContentJson: string;
  baseTitle: string;
  deviceId: string;
};

export type ChapterOutboxItem = {
  id: string;
  bookId: string;
  chapterId: string;
  userId: string;
  title: string;
  contentJson: string;
  wordCount?: number;
  baseRev: number;
  attempts: number;
  nextAttemptAt: number;
  createdAt: number;
};

/** Crash-durable parked conflict (restored on hydrate). */
export type PendingConflictRecord = {
  bookId: string;
  chapterId: string;
  userId: string;
  remoteRev: number;
  mergeJson: string;
  savedAt: number;
};

/**
 * Append-only local record of everything that happened to a chapter body.
 *
 * - `flush`  — the editor handed us new text (checkpointed, not one per keystroke)
 * - `push_ok` — the server accepted it
 * - `push_failed` — the server refused it or the write threw
 * - `offline` — we could not even reach the server
 *
 * Failure entries are never pruned: they are the only local trace of work the
 * server does not have.
 */
export type ChapterJournalKind = 'flush' | 'push_ok' | 'push_failed' | 'offline';

export type ChapterJournalEntry = {
  /** `${userId}::${bookId}::${chapterId}::${seq}` — monotonic within a chapter. */
  key: string;
  seq: number;
  bookId: string;
  chapterId: string;
  userId: string;
  kind: ChapterJournalKind;
  at: number;
  title: string;
  /** Omitted when retention has pruned the body but kept the timeline entry. */
  contentJson?: string;
  wordCount?: number;
  baseRev: number;
  /** Server revision after a successful push. */
  rev?: number;
  error?: string;
  deviceId?: string;
};

export type ChapterJournalRetentionPolicy = {
  /** Keep at most this many body-carrying `flush` entries per chapter. */
  maxBodiesPerChapter: number;
  /** Drop `flush` bodies older than this. Failures ignore it. */
  maxBodyAgeMs: number;
  /** Do not checkpoint a `flush` body more often than this. */
  checkpointEveryMs: number;
};

export const DEFAULT_CHAPTER_JOURNAL_RETENTION: ChapterJournalRetentionPolicy = {
  maxBodiesPerChapter: 40,
  maxBodyAgeMs: 30 * 24 * 60 * 60 * 1000,
  checkpointEveryMs: 30 * 1000,
};

export type ChapterConflictKind =
  | 'both_modified'
  | 'edit_vs_delete'
  | 'delete_vs_edit'
  | 'both_inserted_different';

export type ChapterConflictHunk = {
  id: string;
  blockId: string;
  kind: ChapterConflictKind;
  baseNode: ChapterBlockNode | null;
  mineNode: ChapterBlockNode | null;
  theirsNode: ChapterBlockNode | null;
  baseText: string;
  mineText: string;
  theirsText: string;
  orderHint: number;
};

export type ChapterConflictResolutionChoice = 'mine' | 'theirs' | 'combined';

export type ChapterConflictResolution = {
  hunkId: string;
  choice: ChapterConflictResolutionChoice;
  /** When choice is `combined`, the edited TipTap node or plain text to apply. */
  combinedNode?: ChapterBlockNode | null;
  combinedText?: string;
};

export type ThreeWayMergeInput = {
  base: ChapterDocContent;
  mine: ChapterDocContent;
  theirs: ChapterDocContent;
  baseTitle?: string;
  mineTitle?: string;
  theirsTitle?: string;
};

export type ThreeWayMergeResult = {
  merged: ChapterDocContent;
  mergedTitle: string;
  conflicts: ChapterConflictHunk[];
  autoMergedBlockCount: number;
  /** True when mine equals theirs (or both equal base). */
  identical: boolean;
};

export type ChapterSyncPushResult =
  | {
      ok: true;
      rev: number;
      updatedAt: string;
      title: string;
      contentJson: string;
    }
  | {
      ok: false;
      status: 409;
      remote: {
        title: string;
        content: unknown;
        rev: number;
        updatedAt: string;
      };
    }
  | {
      ok: false;
      status: number;
      error: string;
      offline?: boolean;
    };

export type ChapterSyncPushFn = (input: {
  bookId: string;
  chapterId: string;
  title: string;
  contentJson: string;
  wordCount?: number;
  baseRev: number;
  /** Author save: skip optimistic concurrency and keep this payload. */
  force?: boolean;
}) => Promise<ChapterSyncPushResult>;

export type ChapterSyncFetchRemoteFn = (input: {
  bookId: string;
  chapterId: string;
}) => Promise<{
  title: string;
  content: unknown;
  rev: number;
  updatedAt: string;
} | null>;
